<template>
  <Teleport to="body">
    <Transition name="bounce" appear>
      <div class="session-modal-overlay" role="dialog" aria-modal="true">
        <div class="session-outer">

          <!-- Icon sits above card, centered -->
          <div class="session-icon-circle">
            <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="10" width="16" height="11" rx="2"
                stroke="var(--text-color-black-white)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 10V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V10"
                stroke="var(--text-color-black-white)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="12" cy="15.5" r="1.5" fill="var(--text-color-black-white)"/>
            </svg>
          </div>

          <!-- Card — icon overlaps from above -->
          <div class="session-card">
            <div class="session-text-wrap">
              <p class="session-title">Session Expired</p>
              <p class="session-message">{{ message }}</p>
            </div>

            <button class="session-login-btn" type="button" :disabled="loggingOut" @click="goToLogin">
              {{ loggingOut ? 'Redirecting…' : 'Log In' }}
            </button>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/**
 * SessionExpiredModal — blocking dialog shown once when any API call returns
 * 401/403 (session closed / timed out / account removed).
 *
 * Intentionally NOT dismissible (no backdrop click, no Esc): the session is
 * dead, so the only way forward is the Log In button, which clears local auth
 * state via the `logOut` store action and lands on /sign-in.
 *
 * Shown globally from App.vue, driven by `sessionExpired` in
 * `@/services/error/sessionExpired.js`.
 */
import { ref } from 'vue';
import { useStore } from 'vuex';

defineProps({
  message: {
    type: String,
    default: 'Your session has expired. Please log in again to continue.',
  },
});

const store = useStore();
const loggingOut = ref(false);

async function goToLogin() {
  if (loggingOut.value) return;
  loggingOut.value = true;
  try {
    // logOut clears localStorage/store and ends in
    // window.location.replace('/sign-in')
    await store.dispatch('logOut');
  } catch {
    window.location.replace('/sign-in');
  }
}
</script>

<style lang="scss" scoped>
/* ── Overlay — true full-screen fixed backdrop ──────────────────── */
.session-modal-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(112, 114, 118, 0.80);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 999999; /* above every other modal — session death trumps all */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Outer wrapper — gives room for icon above card ─────────────── */
.session-outer {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 329px;
  max-width: 92vw;
  position: relative;
}

/* ── Icon circle — sits above card ──────────────────────────────── */
.session-icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--quick-link-icon-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--Strokes, #D9E5EC);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  z-index: 2;
  position: relative;
  margin-bottom: -50px; /* overlaps into card top */
  flex-shrink: 0;
}

/* ── Card ────────────────────────────────────────────────────────── */
.session-card {
  width: 100%;
  background: var(--header-bg-light-new, #fff);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
}

/* Text section — padding-top pushes content below overlapping icon */
.session-text-wrap {
  width: 100%;
  padding: 70px 24px 28px; /* top = icon overlap (50px) + gap (20px) */
  text-align: center;
}

.session-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--text-color-white-black, #1a1a1a);
  font-family: 'Poppins', sans-serif;
  margin-bottom: 8px;
}

.session-message {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--text-color-white-black, #1a1a1a);
  opacity: 0.85;
  font-family: 'Poppins', sans-serif;
}

/* Log In button — full width, flat top, rounded bottom */
.session-login-btn {
  width: 100%;
  padding: 8px 0;
  background: var(--quick-link-icon-bg);
  color: var(--text-color-black-white);
  font-size: 18px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  border: none;
  cursor: pointer;
  border-radius: 0 0 20px 20px;
  transition: background 0.15s;
  letter-spacing: 0.3px;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
}

/* ── Bounce animation ───────────────────────────────────────────── */
.bounce-enter-active {
  animation: session-bounce-in 0.45s ease-out;
}
.bounce-leave-active {
  animation: session-bounce-in 0.45s reverse;
}

@keyframes session-bounce-in {
  0%   { opacity: 0; transform: scale(0.8); }
  60%  { opacity: 1; transform: scale(1.04); }
  80%  { transform: scale(0.97); }
  100% { transform: scale(1); }
}
</style>
